import { all, call, delay, fork, put, select, spawn, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { cloneDeep, get, isEmpty, isEqual, sortBy, pick } from 'lodash';
import { PDFDocument } from 'pdf-lib';
import moment from 'moment';
import {
  DELIVERYTYPE_SHIPPING,
  DELIVERYTYPE_STOREPICKUP,
  DOCTYPE_LOCATION,
  DOCTYPE_ORDER_MANAGEMENT_CONTROL_RECORD,
  DOCTYPE_PRINT_TEMPLATE,
  DOCTYPE_SALE_TRANSACTION,
  DOCTYPE_SUBSIDIARY,
} from '@krispsystems/constants';

import * as apiOms from '../../services/order_management/api_oms';
import * as apiCommon from '../../services/apiCommon';
import * as omsActions from '../../actions/action_creator_oms';
import * as commonActions from '../../actions/actionCreatorsCommon';

import {
  ASSIGN_WAVE_TO_CONTROL_RECORD,
  BOOK_SHIPPING_CONTROL_RECORD,
  BOOK_SHIPPING_DONE_CONTROL_RECORD,
  BOOK_SHIPPING_ERROR_CONTROL_RECORD,
  BOOK_SHIPPING_IN_PROGRESS_CONTROL_RECORD,
  CANCEL_BOOK_SHIPPING_CONTROL_RECORD,
  CANCEL_BOOK_SHIPPING_DONE_CONTROL_RECORD,
  CANCEL_BOOK_SHIPPING_ERROR_CONTROL_RECORD,
  CANCEL_BOOK_SHIPPING_IN_PROGRESS_CONTROL_RECORD,
  GET_OMS_CONTROL_RECORDS,
  HOLD_CONTROL_RECORD,
  PRINT_CONTROL_RECORD_DONE,
  PRINT_CONTROL_RECORD_ERROR,
  PRINT_CONTROL_RECORD_IN_PROGRESS,
  PRINT_CONTROL_RECORD,
  REALLOCATE_CONTROL_RECORD,
  SAVE_CONTROL_RECORDS,
  PICKEDUP_CONTROL_RECORDS,
  SHIPPED_CONTROL_RECORDS,
} from '../../constants/constant_oms';
import { COMMON_PROMPT_MODAL_SIGNAL } from '../../constants/constant_action_types';

import {
  getBook,
  getControlRecords,
  getFilter,
  getPackList,
  getPickList,
  getSelectedPackShip,
} from '../../components/admin/order_management/OrderManagementSelector';
import { getParticularPreferenceSettings } from '../../common/selectors/preference';

import { clearOmsLoading } from '../../actions/action_creator_oms';
import { addErrorMessages } from '../../actions/actionCreatorsAdmin';

import { buildTransactionTemplate, getPrintTemplateId } from '../export_transaction/saga_export_transaction';
import { cancelShippingOrder, confirmShippingOrders, createShippingOrders } from '../../services/shipping/api_shipping';

import { getErrorMessage } from '../../utils';
import Handlebars from '../../utils/handlebar';
import { incrementProgress, resetProgress, setTotalProgress } from '../../actions/action_creator_progress';
import { browserHistory } from 'react-router';
import { extractControlRecord } from '../../components/admin/order_management/control_record/utils';
import { getCurrentUser } from '../../common/selectors/selector_common';

// Will most likely be moved to ks-shipping
export default function* (app) {
  try {
    yield all([
      fork(assignWaveToControlRecord, app),
      fork(bookShippingControlRecords, app),
      fork(cancelBookShippingControlRecords, app),
      fork(fetchControlRecordSaga, app),
      fork(holdControlRecord, app),
      fork(printControlRecord, app),
      fork(reallocateControlRecord, app),
      fork(saveControlRecords, app),
      fork(pickedUpOrShippedControlRecords, app),
    ]);
  } catch (e) {
    console.error('@saga_order_management_control_record: ', e.message || e.stack);
    throw e;
  }
}

function getActionOrigin() {
  const urlPath = window.location.pathname;
  const fromPack = urlPath.indexOf('pack_ship') > -1;
  const fromPick = urlPath.indexOf('pick') > -1;
  return {
    fromPack,
    fromPick,
    fromOrder: !fromPick && !fromPack,
  };
}

function* fetchControlRecordSaga(app) {
  yield takeLatest(GET_OMS_CONTROL_RECORDS, callFetchControlRecordSaga, app);
}

function* callFetchControlRecordSaga(app, action) {
  const { data } = action;
  try {
    if (data.created_from) {
      data.created_from = moment(data.created_from).startOf('day').toISOString();
    }

    if (data.created_to) {
      data.created_to = moment(data.created_to).endOf('day').toISOString();
    }

    if (!data.rows_per_page && (data.sale_ids || []).length) {
      data.rows_per_page = data.sale_ids.length;
    }

    data.abandon = false;
    data.ship = false;

    const dataParams = {
      filters: data,
      list_type: 'oms/control_record/sale',
      methodType: 'post',
    };

    yield put(omsActions.resetSelectedInitialLines());
    yield put(omsActions.resetSelectedPickLines());
    yield put(omsActions.resetSelectedPackShipLines());
    const tableData = data.location_id ? yield call(apiCommon.getDocs, app, dataParams) : { ...data, data: [] };

    const { fromPack } = getActionOrigin();
    if (fromPack) {
      yield put(omsActions.groupPackShipLines(tableData.data));
    }
    yield put(omsActions.setOmsControlRecords({ ...data, ...tableData }));
  } catch (error) {
    const errMsg = getErrorMessage(error);
    yield put(addErrorMessages([errMsg]));
    yield put(omsActions.fetchErrorOmsControlRecords(errMsg));
  }
}

function* holdControlRecord(app) {
  yield takeLatest(HOLD_CONTROL_RECORD, callHoldControlRecordSaga, app);
}

function* callHoldControlRecordSaga(app, action) {
  try {
    const { data, unhold } = action;

    if (unhold) {
      yield put(commonActions.showPromptModal('Unhold Order', 'Are you sure you want to unhold order/s?', false));
      const { signal } = yield take(COMMON_PROMPT_MODAL_SIGNAL);
      if (!signal) {
        yield put(clearOmsLoading());
        return false;
      }
    } else {
      yield put(commonActions.showPromptModal('Hold Order', 'Are you sure you want to hold order/s?', false));
      const { signal } = yield take(COMMON_PROMPT_MODAL_SIGNAL);
      if (!signal) {
        yield put(clearOmsLoading());
        return false;
      }
    }

    const result = yield call(apiOms.holdItemLine, app, data);
    yield call(hardRefreshList, result);

  } catch (error) {
    yield put(clearOmsLoading());
    console.error('callHoldControlRecordSaga: error', error);
  }
}

function* reallocateControlRecord(app) {
  yield takeLatest(REALLOCATE_CONTROL_RECORD, callReallocateControlRecordSaga, app);
}

function* callReallocateControlRecordSaga(app, action) {
  try {
    const { data } = action;

    yield put(commonActions.showPromptModal('Reject Order', 'Are you sure you want to reject order/s?', false));
    const { signal } = yield take(COMMON_PROMPT_MODAL_SIGNAL);
    if (!signal) {
      yield put(clearOmsLoading());
      return false;
    }
    const hasOmsIgnoreLocationFind = data.some((d) => (d.st_item_line || {}).oms_ignore_location_find);
    if (hasOmsIgnoreLocationFind) {
      yield delay(1000);
      yield put(
        commonActions.showPromptModal(
          'Reject Order',
          'Selected order/s has OMS ignore location find and will not be rejected.',
        ),
      );
      const { signal } = yield take(COMMON_PROMPT_MODAL_SIGNAL);
      if (!signal) {
        yield put(clearOmsLoading());
        return false;
      }
    }

    const currList = yield call(fetchControlRecords, app, action);
    const hasAbandonedStatus = currList.some(({ abandon }) => abandon);
    const hasShippedStatus = currList.some(({ ship }) => ship);

    if (hasAbandonedStatus) {
      yield put(
        commonActions.showPromptModal(
          'Reject Order',
          'Selected orders contains status that has already been abandoned',
        ),
      );
      return false;
    }

    if (hasShippedStatus) {
      yield put(
        commonActions.showPromptModal(
          'Reject Order',
          'Selected orders contains status that has already been shipped',
        ),
      );
      return false;
    }

    const rejectControlRecords = data.filter((controlRecord) => {
      return !controlRecord.st_item_line.oms_ignore_location_find;
    });

    const result = yield call(apiOms.reallocateControlRecord, app, rejectControlRecords);
    yield call(hardRefreshList, result);
  } catch (error) {
    yield put(clearOmsLoading());
    console.error('callReallocateControlRecordSaga: error', error);
  }
}

function* assignWaveToControlRecord(app) {
  yield takeLatest(ASSIGN_WAVE_TO_CONTROL_RECORD, callAssignWaveToControlRecordSaga, app);
}

function* callAssignWaveToControlRecordSaga(app, action) {
  const { data } = action;
  try {
    yield put(
      commonActions.showPromptModal(
        'Assign Wave',
        'Are you sure you want to assign a wave number to the selected order/s?',
        false,
      ),
    );

    const { signal } = yield take(COMMON_PROMPT_MODAL_SIGNAL);
    if (!signal) {
      yield put(clearOmsLoading());
      return false;
    }

    const { reference_id } = yield call(apiOms.getWaveReference, app, {});

    const waveData = data.map((d) => {
      d.line_picking_reference = reference_id;
      return d;
    });

    const result = yield call(apiOms.updateControlRecords, app, waveData);
    yield call(refreshList, result);

  } catch (error) {
    yield put(clearOmsLoading());
    console.error('callAssignWaveToControlRecordSaga: error', error);
  }
}

function* hardRefreshList(controlRecords) {
  return yield call(refreshList, controlRecords, true);
}

function* refreshList(controlRecords, hardRefresh) {
  const { fromPick, fromPack, fromOrder } = getActionOrigin();
    if (fromPack) {
      const packList = yield select(getPackList);
      const lines = yield call(getUpdatedPackShipList, packList, controlRecords);
      yield put(omsActions.resetSelectedPackShipLines());
      yield put(omsActions.groupPackShipLines(lines));
      yield put(clearOmsLoading());
      return lines;
    } else if (fromPick) {
      const pickList = yield select(getPickList);
      const lines = yield call(getUpdatedPickList, pickList, controlRecords);
      yield put(omsActions.resetSelectedPickLines());
      yield put(omsActions.groupPickLines(lines));
      yield put(clearOmsLoading());
      return lines;
    } else if (fromOrder) {
      if (hardRefresh) {
        const filterData = yield select(getFilter);
        yield put(omsActions.fetchOmsControlRecords(filterData));
        yield put(clearOmsLoading());
      } else {
        const tableData = yield select(getControlRecords);
        const lines = yield call(getUpdatedPackShipList, tableData, controlRecords);
        yield put(omsActions.setOmsControlRecords({ data: lines }));
        yield put(omsActions.resetSelectedInitialLines());
        yield put(clearOmsLoading());
        return lines;
      }
    }
}

function* pickedUpOrShippedControlRecords(app) {
  yield takeLatest([PICKEDUP_CONTROL_RECORDS, SHIPPED_CONTROL_RECORDS], callPickedUpOrShippedControlRecords, app);
}

function* callPickedUpOrShippedControlRecords(app, action) {
  try {
    const { ship, is_pickup_store, data } = action;
    if (ship && is_pickup_store) {
      const isPickUpValid = yield call(pickupHandler, app, action);
      if (!isPickUpValid) {
        yield put(clearOmsLoading());

        return;
      }
    } else if (ship) {
      const isShipValid = yield call(shippedHandler, app, action);
      if (!isShipValid) {
        yield put(clearOmsLoading());
        return;
      }
    }

    yield put(setTotalProgress(data.length));

    const actualResult = [];

    for (const d of data) {
      try {
        const pickedUpOrShippedData = yield call(updateStatus, [d], false, false, true);
        const result = yield call(apiOms.updateControlRecords, app, pickedUpOrShippedData);
        actualResult.push(result[0]);
        yield put(incrementProgress());
      } catch (e) {
        console.error('e', e);
      }
    }
    yield delay(500);
    const lines = yield call(refreshList, actualResult);

    if (isEmpty(lines)) {
      browserHistory.push('/admin/order_management/order');
    }

    yield put(resetProgress());
  } catch (err) {
    console.error('err', err);
  }
}

function* saveControlRecords(app) {
  yield takeLatest(SAVE_CONTROL_RECORDS, callSaveControlRecordsSaga, app);
}

function* shippedHandler(app, action) {
  const { data: list, inline } = action;
  const locationId = list[0].location_id;
  const subsidiaryId = list[0].subsidiary_id;
  const { value: omsCloseWithoutShipper } = yield select(
    getParticularPreferenceSettings,
    'oms_close_order_without_shipper',
    ['value'],
    locationId,
    subsidiaryId,
  );

  if (isEqual(omsCloseWithoutShipper, 'false')) {
    yield put(
      commonActions.showPromptModal(
        'Ship To Customer',
        'Are you sure you want to mark orders as shipped? Only orders with attached tracking details will be marked as shipped.',
        false,
      ),
    );
  } else {
    yield put(
      commonActions.showPromptModal('Ship To Customer', 'Are you sure you want to mark orders as shipped?', false),
    );
  }

  const { signal } = yield take(COMMON_PROMPT_MODAL_SIGNAL);
  if (!signal) {
    return false;
  }

  const currList = yield call(fetchControlRecords, app, action);
  const hasInvalidStatus = currList.some(({ ship }) => ship);
  const hasAbandonedStatus = currList.some(({ abandon }) => abandon);

  if (hasInvalidStatus) {
    yield put(
      commonActions.showPromptModal(
        'Ship To Customer Error',
        'Selected orders contains status that has already been marked as shipped',
      ),
    );

    return;
  }

  if (hasAbandonedStatus) {
    yield put(
      commonActions.showPromptModal(
        'Ship To Customer Error',
        'Selected orders contains status that has already been abandoned',
      ),
    );

    return;
  }

  if (inline) {
    const shippedData = yield call(updateStatus, list, false, false, true);
    const result = yield call(apiOms.updateControlRecords, app, shippedData);

    yield call(refreshList, result);
  }

  return true;
}

function* pickupHandler(app, action) {
  const { data: list, inline } = action;

  yield put(
    commonActions.showPromptModal('Pick-Up in Store', 'Are you sure you want to mark orders as processed?', false),
  );
  const { signal } = yield take(COMMON_PROMPT_MODAL_SIGNAL);
  if (!signal) {
    return false;
  }

  const currList = yield call(fetchControlRecords, app, action);
  const hasInvalidStatus = currList.some(({ ship }) => ship);
  const hasAbandonedStatus = currList.some(({ abandon }) => abandon);

  if (hasInvalidStatus) {
    yield put(
      commonActions.showPromptModal('Pick-Up Error', 'Selected orders contains status that has already been processed'),
    );

    return false;
  }

  if (hasAbandonedStatus) {
    yield put(
      commonActions.showPromptModal('Pick-Up Error', 'Selected orders contains status that has already been abandoned'),
    );

    return false;
  }

  if (inline) {
    const pickedUpData = yield call(updateStatus, list, false, false, true);
    const result = yield call(apiOms.updateControlRecords, app, pickedUpData);

    yield call(refreshList, result);
  }

  return true;
}

function* packHandler(app, action) {
  const { data: list } = action;

  const currList = yield call(fetchControlRecords, app, action);
  const hasInvalidStatus = currList.some(({ pack }) => pack);
  const hasAbandonedStatus = currList.some(({ abandon }) => abandon);
  const hasSplitQty = list.some(({ picked_quantity, quantity }) => {
    if (picked_quantity) {
      return Number(picked_quantity) < Number(quantity);
    }
    return false;
  });

  if (hasInvalidStatus) {
    yield put(
      commonActions.showPromptModal('Pack Error', 'Selected orders contains status that has already been packed'),
    );
    return;
  }

  if (hasAbandonedStatus) {
    yield put(
      commonActions.showPromptModal('Pack Error', 'Selected orders contains status that has already been abandoned'),
    );
    return;
  }

  if (hasSplitQty) {
    const message = 'Selected orders contains picked quantity less than the order quantity. Are you sure you want to proceed?';
    yield put(commonActions.showPromptModal('Pack Order', message, false));
    const { signal } = yield take(COMMON_PROMPT_MODAL_SIGNAL);
    if (!signal) {
      return;
    }
  }

  const packedData = yield call(updateStatus, list, false, true, false);
  const result = yield call(apiOms.updateControlRecords, app, packedData);

  yield call(refreshList, result);

  /**
   * Send SMS To customer that the item has been packed and ready to be picked-up
   * Only supports pickup from store at the moment
   */
  const pickupOrders = [];

  packedData.forEach((order) => {
    const { delivery_type } = order || {};
    const { cid } = delivery_type || {};

    switch (cid) {
      case DELIVERYTYPE_STOREPICKUP:
        pickupOrders.push(order);
        break;

      default:
        break;
    }
  });

  if (pickupOrders.length) {
    const locationId = pickupOrders[0].location_id;
    const subsidiaryId = pickupOrders[0].subsidiary_id;

    const { value: isSendPackedPickupSms } = yield select(
      getParticularPreferenceSettings,
      'oms_send_pack_sms_pickup',
      ['value'],
      locationId,
      subsidiaryId,
    );

    const { value: sendPackedSmsTemplateId } = yield select(
      getParticularPreferenceSettings,
      'oms_sms_customer_packed_pickup',
      ['value'],
      locationId,
      subsidiaryId,
    );

    if (JSON.parse(isSendPackedPickupSms || false) && sendPackedSmsTemplateId) {
      const sendPackedSmsTemplate = yield call(apiCommon.getDoc, app, {
        id: sendPackedSmsTemplateId,
        docType: DOCTYPE_PRINT_TEMPLATE,
      });

      const [location, subsidiary] = yield all([
        call(apiCommon.getDoc, app, {
          id: locationId,
          docType: DOCTYPE_LOCATION,
        }),
        call(apiCommon.getDoc, app, {
          id: subsidiaryId,
          docType: DOCTYPE_SUBSIDIARY,
        }),
      ]);

      const compiler = Handlebars.compile(sendPackedSmsTemplate.template_text);

      function* sendPackedSms(saleId, controlRecords) {
        const sale = yield call(apiCommon.getDoc, app, {
          id: saleId,
          docType: DOCTYPE_SALE_TRANSACTION,
        });

        const { customer } = sale;

        if (!customer) return;

        const templateData = yield call(buildTransactionTemplate, app, sale);
        const transaction = {
          location,
          subsidiary,
          customer: templateData.customer,
          meta: templateData.meta,
          transaction: {
            created_datetime: templateData.transaction.created_datetime,
            currency: templateData.transaction.currency,
            customer: templateData.customer,
            id: templateData.transaction.id,
            location: templateData.location,
            oms_control_records: controlRecords,
            reference_id: templateData.transaction.reference_id,
            sale: templateData.transaction,
            status: templateData.transaction.status,
            subsidiary_id: templateData.transaction.subsidiary_id,
            subsidiary: templateData.subsidiary,
          },
        };

        const { contact_numbers } = transaction.customer;

        if (!contact_numbers || !contact_numbers.length) return;

        const primaryContactNumber = contact_numbers.find(({ primary }) => primary);

        if (!primaryContactNumber) return;

        const { value: contactNumber } = primaryContactNumber;

        const message = compiler(transaction);
        const smsOpts = {
          destination_number: contactNumber,
          content: message,
        };

        const smsParams = {
          id: customer.id,
          subsidiary: subsidiaryId,
          location: locationId,
        };

        yield spawn(apiCommon.sms, app, smsOpts, smsParams);
      }

      const groupBySales = pickupOrders.reduce((grouped, order) => {
        const { sale_transaction_id } = order;
        const orders = grouped.get(sale_transaction_id) || [];
        orders.push(order);
        grouped.set(sale_transaction_id, orders);
        return grouped;
      }, new Map());

      for (const [saleId, controlRecords] of groupBySales) {
        yield spawn(sendPackedSms, saleId, controlRecords);
      }
    }
  }
}

function* pickHandler(app, action) {
  const { data: list } = action;

  const currList = yield call(fetchControlRecords, app, action);
  const hasInvalidStatus = currList.some(({ pick }) => pick);
  const hasAbandonedStatus = currList.some(({ abandon }) => abandon);
  const hasSplitQty = list.some(({ picked_quantity, quantity }) => {
    if (picked_quantity) {
      return picked_quantity < quantity;
    }
    return false;
  });

  if (hasInvalidStatus) {
    yield put(
      commonActions.showPromptModal('Pick Error', 'Selected orders contains status that has already been picked'),
    );
    return;
  }

  if (hasAbandonedStatus) {
    yield put(
      commonActions.showPromptModal('Pick Error', 'Selected orders contains status that has already been abandoned'),
    );
    return;
  }

  if (hasSplitQty) {
    const message = 'Selected orders contains picked quantity less than the order quantity. Are you sure you want to proceed?';
    yield put(commonActions.showPromptModal('Pick Order', message, false));
    const { signal } = yield take(COMMON_PROMPT_MODAL_SIGNAL);
    if (!signal) {
      return;
    }
  }

  const pickedData = yield call(updateStatus, list, true);
  const result = yield call(apiOms.updateControlRecords, app, pickedData);

  yield call(refreshList, result);
}

function* fetchControlRecords(app, action) {
  const { data } = action;
  const filters = data.reduce(
    (prevVal, currVal) => {
      prevVal.oms_ids.push(currVal.id);
      prevVal.sale_ids.push(currVal.sale_transaction_id);
      return prevVal;
    },
    { oms_ids: [], sale_ids: [] },
  );

  const dataParams = {
    filters,
    list_type: 'oms/control_record/sale',
    methodType: 'post',
  };

  const tableData = yield call(apiCommon.getDocs, app, dataParams);
  return extractControlRecord(tableData.data);
}

function* callSaveControlRecordsSaga(app, action) {
  const { pick, pack, ship, is_pickup_store, direct, data } = action;
  try {
    if (pick) {
      yield call(pickHandler, app, action);
      yield put(clearOmsLoading());
      return;
    }

    if (pack) {
      yield call(packHandler, app, action);
      yield put(clearOmsLoading());
      return;
    }

    if (ship) {
      if (is_pickup_store) {
        yield call(pickupHandler, app, { ...action, inline: true });
        yield put(clearOmsLoading());
        return;
      }
      yield call(shippedHandler, app, { ...action, inline: true });
      yield put(clearOmsLoading());
    }

    if (direct) {
      yield call(apiOms.updateControlRecords, app, data);
    }
  } catch (error) {
    yield put(clearOmsLoading());
    console.error('callSaveControlRecordsSaga: error', error);
  }
}

function* bookShippingControlRecords(app) {
  yield takeLatest(BOOK_SHIPPING_CONTROL_RECORD, bookShippingControlRecordsSaga, app);
}

function* bookShippingControlRecordsSaga(app, action) {
  const { payload } = action;
  const { data, location, subsidiary } = payload;
  const lines = Array.isArray(data) ? data : [data];

  const locationId = location.id;
  const locationName = location.name;
  const subsidiaryId = subsidiary.id;

  const creation = [];
  const confirmation = [];

  for (const line of lines) {
    const { customer, delivery_address, delivery_note, oms_control_records, reference_id, shipping_method } = line;

    const { id: shippingMethodId } = shipping_method || {};

    const itemShippingLineId = get(oms_control_records, '[0].st_item_ship_line_id');

    const trackingNumber = get(oms_control_records, '[0].st_item_ship_line.delivery_tracking_number');

    if (!shippingMethodId || !itemShippingLineId || trackingNumber) continue;

    const { isBooking } = yield select(getBook, reference_id);

    if (isBooking) continue;

    yield put({
      type: BOOK_SHIPPING_IN_PROGRESS_CONTROL_RECORD,
      payload: { id: reference_id },
    });

    const order = {
      item_line_id: itemShippingLineId,
      sale_reference_id: reference_id,
    };

    if (!trackingNumber) {
      if (delivery_address) {
        const {
          address1,
          address2,
          address3,
          addressee,
          city,
          contact_number,
          country,
          state,
          zipcode,
        } = delivery_address;

        const resolveDeliveryAddress = {
          address1,
          address2,
          address3,
          addressee,
          city,
          state,
          zipcode,
          country_code: country.code,
          country: country.name,
        };

        if (contact_number) {
          resolveDeliveryAddress.contact_number = contact_number.replace(/-/g, '');
        }

        order.delivery_address = resolveDeliveryAddress;
      }

      if (customer) {
        const { name, email, first_name, last_name, contact_numbers } = customer;

        const resolvedCustomer = {
          contact_numbers,
          email,
          first_name,
          last_name,
          name,
        };

        order.customer = resolvedCustomer;
      }

      order.items = oms_control_records.map((controlRecord) => {
        const { item, st_item_line } = controlRecord;

        const { quantity, actual_sale_price } = st_item_line || {};
        const parsedQuantity = Number(quantity || 0);
        const parsedActualSalePrice = Number(actual_sale_price || 0);
        const price = parsedActualSalePrice / quantity;

        const orderItem = {
          price,
          name: item.name,
          quantity: parsedQuantity,
          reference_id: item.number,
          weight: Number(item.weight || 1),
          meta: {
            location: locationName,
          },
        };

        return orderItem;
      });

      order.delivery_note = delivery_note;
      order.shipping_method_id = shippingMethodId;
      creation.push(order);
    } else {
      order.tracking_number = trackingNumber;
      confirmation.push(order);
    }
  }

  if (creation.length) {
    yield fork(bookGroupedOrders, app, creation, locationId, subsidiaryId);
  }

  if (confirmation.length) {
    yield fork(bookGroupedOrders, app, confirmation, locationId, subsidiaryId, true);
  }
}

function* bookGroupedOrders(app, orders, locationId, subsidiaryId, isConfirmation) {
  try {
    const bookedOrdersResParams = {
      locationId,
      subsidiaryId,
      isBook: true,
    };

    let ordersRes;

    if (isConfirmation) {
      ordersRes = yield call(confirmShippingOrders, app, orders, bookedOrdersResParams);
    } else {
      ordersRes = yield call(createShippingOrders, app, orders, bookedOrdersResParams);
    }

    const { orders: bookedOrders, integration_type } = ordersRes;

    const list = yield select(getPackList);

    for (const order of bookedOrders) {
      const {
        message,
        sale_reference_id,
        courier,
        manifest_url,
        manifest,
        packing_slip_url,
        shipping_label_url,
        price,
        link,
        tracking_number,
        status,
      } = order;

      if (message) {
        yield put({
          type: BOOK_SHIPPING_ERROR_CONTROL_RECORD,
          payload: {
            id: sale_reference_id,
            error: message,
          },
        });

        continue;
      }

      const index = list.findIndex(({ reference_id }) => reference_id === sale_reference_id);

      const shippingDetails = {
        courier,
        manifest_url,
        manifest,
        packing_slip_url,
        shipping_label_url,
        delivery_cost: price,
        delivery_link: link,
        delivery_tracking_number: tracking_number,
        freight_aggregator: (integration_type || {}).name,
        freight_status: status,
      };

      const item = cloneDeep(list[index]);
      item.oms_control_records = item.oms_control_records.map((controlRecord) => ({
        ...controlRecord,
        st_item_ship_line: {
          ...controlRecord.st_item_ship_line,
          ...shippingDetails,
        },
      }));

      const isSelected = yield select(getSelectedPackShip, item.id);

      if (isSelected) {
        yield put(omsActions.setPackShipLine(item));
      }

      yield put({
        type: BOOK_SHIPPING_DONE_CONTROL_RECORD,
        payload: {
          index,
          item,
          id: sale_reference_id,
        },
      });
    }
  } catch (err) {
    const errorMessage = getErrorMessage(err);

    for (const order of orders) {
      yield put({
        type: BOOK_SHIPPING_ERROR_CONTROL_RECORD,
        payload: {
          id: order.sale_reference_id,
          error: errorMessage,
        },
      });
    }
  }
}

function* cancelBookShippingControlRecords(app) {
  yield takeLatest(CANCEL_BOOK_SHIPPING_CONTROL_RECORD, cancelBookShippingControlRecordsSaga, app);
}

function* cancelBookShippingControlRecordsSaga(app, action) {
  const { payload } = action;
  const { data, location, subsidiary } = payload;
  const lines = Array.isArray(data) ? data : [data];

  const locationId = location.id;
  const subsidiaryId = subsidiary.id;

  for (const line of lines) {
    const { reference_id, oms_control_records } = line;
    const { isCancelBooking } = yield select(getBook, reference_id);

    if (isCancelBooking) continue;

    const trackingNumber = get(oms_control_records, '[0].st_item_ship_line.delivery_tracking_number');

    if (!trackingNumber) continue;

    const itemLineId = get(oms_control_records, '[0].st_item_ship_line_id');
    const data = {
      itemLineId,
      locationId,
      subsidiaryId,
      trackingNumber,
    };

    yield fork(cancelBookShipping, app, data, reference_id);
  }
}

function* cancelBookShipping(app, data, saleReference) {
  try {
    yield put({
      type: CANCEL_BOOK_SHIPPING_IN_PROGRESS_CONTROL_RECORD,
      payload: { id: saleReference },
    });

    const { locationId, trackingNumber, subsidiaryId } = data;

    yield call(cancelShippingOrder, app, trackingNumber, {
      locationId,
      subsidiaryId,
    });

    const list = yield select(getPackList);
    const index = list.findIndex(({ reference_id }) => reference_id === saleReference);

    const item = cloneDeep(list[index]);
    item.oms_control_records = item.oms_control_records.map((controlRecord) => ({
      ...controlRecord,
      st_item_ship_line: {
        ...controlRecord.st_item_ship_line,
        courier_job_id: '',
        courier: '',
        delivery_cost: 0,
        delivery_link: '',
        delivery_tracking_number: '',
        freight_aggregator: '',
        freight_status: '',
        manifest_url: '',
        manifest: '',
        packing_slip_url: '',
        shipping_label_url: '',
      },
    }));

    const isSelected = yield select(getSelectedPackShip, item.id);

    if (isSelected) {
      yield put(omsActions.setPackShipLine(item));
    }

    yield put({
      type: CANCEL_BOOK_SHIPPING_DONE_CONTROL_RECORD,
      payload: { index, item, id: saleReference },
    });
  } catch (err) {
    const errorMessage = getErrorMessage(err);

    yield put({
      type: CANCEL_BOOK_SHIPPING_ERROR_CONTROL_RECORD,
      payload: { id: saleReference, error: errorMessage },
    });
  }
}

function getUpdatedPickList(currList, updatedList) {
  const controlRecords = currList.reduce((arr, val) => {
    const { oms_control_records } = val;
    return arr.concat(oms_control_records);
  }, []);
  const arr = controlRecords.concat(updatedList);
  const map = new Map(arr.map((obj) => [obj.id, obj]));
  const copiedList = Array.from(map.values());
  return copiedList.filter((val) => {
    if (!val.abandon && !val.ship) {
      val.update_time = moment().format();
      return val;
    }
    return false;
  });
}

function getUpdatedPackShipList(omsRecordsBySale, updatedOmsRecords) {
  const updatedList = [];
  omsRecordsBySale.forEach((currentSale) => {
    const saleControlRecords = currentSale.oms_control_records;
    const controlRecordMap = new Map(saleControlRecords.map((obj) => [obj.id, obj]));

    updatedOmsRecords.forEach((omsRecord) => {
      if (omsRecord.sale_transaction_id === currentSale.id) {
        if (omsRecord.ship || omsRecord.abandon) {
          controlRecordMap.delete(omsRecord.id);
        } else {
          controlRecordMap.set(omsRecord.id, omsRecord);
        }
      }
    });

    if (controlRecordMap.size > 0) {
      const updatedSale = {
        ...currentSale,
        oms_control_records: Array.from(controlRecordMap.values()),
      };
      updatedList.push(updatedSale);
    }
  });
  const copiedList = JSON.parse(JSON.stringify(updatedList));
  return copiedList.map((val) => {
    val.update_time = moment().format();
    val.oms_control_records = val.oms_control_records.map((cr) => {
      cr.update_time = moment().format();
      return cr;
    });
    return val;
  });
}

function updateStatus(list = [], pick = false, pack = false, ship = false) {
  return list.map((d) => {
    if (pick) {
      d.pick = true;
      d.pick_datetime = moment().utc();

      d.pack = false;
      d.ship = false;
    } else if (pack) {
      d.pack = true;
      d.pack_datetime = moment().utc();

      d.pick = false;
      d.ship = false;
    } else if (ship) {
      d.ship = true;
      d.ship_datetime = moment().utc();

      d.pick = false;
      d.pack = false;
    }
    return d;
  });
}

const OMS_RECORD_PRINT_LINK_MAP = {
  [DELIVERYTYPE_SHIPPING]: {
    label: 'oms_control_records[0].st_item_ship_line.shipping_label_url',
    pack: 'oms_control_records[0].st_item_ship_line.packing_slip_url',
    pickup: 'oms_control_records[0].st_item_ship_line.manifest_url',
  },
  [DELIVERYTYPE_STOREPICKUP]: {},
};

const OMS_RECORD_PREFERENCE_PRINT_ALL_MAP = {
  [DELIVERYTYPE_SHIPPING]: 'oms_print_all_ship',
  [DELIVERYTYPE_STOREPICKUP]: 'oms_print_all_pickup',
};

function* printControlRecord(app) {
  yield takeEvery(PRINT_CONTROL_RECORD, printControlRecordSaga, app);
}

function* printControlRecordSaga(app, action) {
  const { payload } = action;
  const { deliveryType, isUseShippingIntegration, lines, location, subsidiary, types } = payload;

  yield put({
    type: PRINT_CONTROL_RECORD_IN_PROGRESS,
    payload: { deliveryType, types },
  });

  try {
    const locationId = location.id;
    const subsidiaryId = subsidiary.id;
    const isShipping = deliveryType === DELIVERYTYPE_SHIPPING;
    let isPrintAll = false;

    if (types.includes('label')) {
      const preferencePrintSaleReceiptSetting = OMS_RECORD_PREFERENCE_PRINT_ALL_MAP[deliveryType];
      const { value } = yield select(
        getParticularPreferenceSettings,
        preferencePrintSaleReceiptSetting,
        ['value'],
        locationId,
        subsidiaryId,
      );

      isPrintAll = JSON.parse(value || false);
    }

    const [locationData, subsidiaryData] = yield all([
      call(apiCommon.getDoc, app, {
        id: locationId,
        docType: DOCTYPE_LOCATION,
      }),
      call(apiCommon.getDoc, app, {
        id: subsidiaryId,
        docType: DOCTYPE_SUBSIDIARY,
      }),
    ]);

    const currentUser = yield select(getCurrentUser);

    const templateDataMap = yield call(buildLinesTemplateDataMap, app, {
      currentUser,
      deliveryType,
      isPrintAll,
      isUseShippingIntegration,
      lines,
      types,
      location: locationData,
      subsidiary: subsidiaryData,
    });

    const templateIdMap = yield call(buildTemplateIdMap, {
      deliveryType,
      isPrintAll,
      locationId,
      subsidiaryId,
      types,
    });

    const sortedLines = sortBy(lines, (line) => get(line, 'shipping_method.name'));

    const controlRecordsToSave = new Map();

    const processPdfsByType = async (type) => {
      const templateId = templateIdMap.get(type);
      const pdfDataMap = new Map();
      const pdfManifestDataMap = new Map();

      sortedLines.forEach((line, l) => {
        const { id, oms_control_records } = line;
        const transaction = templateDataMap.get(id);
        const params = { templateId, transaction, oms_control_records, type };

        if (isShipping && isUseShippingIntegration) {
          params.link = get(line, OMS_RECORD_PRINT_LINK_MAP[deliveryType][type]);
          if (!params.link) return;
        }

        pdfDataMap.set(`${type}:${id}`, params);

        if (isPrintAll) {
          pdfDataMap.set(`${type}:sale:${id}`, {
            transaction,
            templateId: templateIdMap.get('sale'),
          });

          if (isShipping) {
            const link = get(line, OMS_RECORD_PRINT_LINK_MAP[DELIVERYTYPE_SHIPPING]['pickup']);

            if (link) pdfManifestDataMap.set(`${type}:pickup:${link}`, { link });
          }
        }
      });

      const processes = [...pdfManifestDataMap, ...pdfDataMap].map(async ([_, data]) => {
        const pdf = await processPdf(app, data);

        if (!pdf) return null;

        if (data.type) {
          data.oms_control_records.forEach((controlRecord) => {
            const { id } = controlRecord;
            const controlRecordToSave = controlRecordsToSave.get(id) || {
              ...controlRecord,
            };

            if (type === 'pack') {
              controlRecordToSave.pack_slip_created_datetime = moment().utc().format();
            }

            if (type === 'label') {
              controlRecordToSave.ship_label_created_datetime = moment().utc().format();

              if (isPrintAll) {
                controlRecordToSave.pack_slip_created_datetime = moment().utc().format();
              }
            }

            controlRecordsToSave.set(id, controlRecordToSave);
          });
        }

        return pdf;
      });

      const pdfs = await Promise.all(processes);
      const fileName = `${type.toUpperCase()} - ${locationData.name} - ${new Date().toLocaleString()}`;
      const pdfsStatus = await printPdfs(pdfs, fileName);
      return pdfsStatus;
    };

    const pdfsByTypeStatus = yield all(types.map(processPdfsByType));
    const isFailed = pdfsByTypeStatus.every((success) => !success);

    if (isFailed) throw new Error('No labels to print');

    const newControlRecords = Array.from(controlRecordsToSave.values()).map((cr) => {
      return pick(cr, ['id', 'pack_slip_created_datetime', 'ship_label_created_datetime']);
    });
    const result = yield call(apiOms.updateControlRecords, app, newControlRecords);
    yield call(refreshList, result);
    yield put({
      type: PRINT_CONTROL_RECORD_DONE,
      payload: { deliveryType, types },
    });
  } catch (err) {
    yield put({
      type: PRINT_CONTROL_RECORD_ERROR,
      payload: {
        deliveryType,
        types,
        message: err.message,
      },
    });
  }
}

function* buildLinesTemplateDataMap(app, params) {
  const {
    currentUser,
    deliveryType,
    isPrintAll,
    isUseShippingIntegration,
    lines,
    location,
    subsidiary,
    types,
  } = params;

  function* getTemplateData(line, type) {
    const link = get(line, OMS_RECORD_PRINT_LINK_MAP[deliveryType][type]);
    const isFetchSale = isPrintAll || !(isUseShippingIntegration && type !== 'pickup' && link);

    if (!isFetchSale) return null;

    const sale = yield call(apiCommon.getDoc, app, {
      id: line.id,
      docType: DOCTYPE_SALE_TRANSACTION,
    });

    if (!sale) return null;

    const templateData = yield call(buildTransactionTemplate, app, sale);

    const transaction = {
      location,
      subsidiary,
      customer: templateData.customer,
      meta: {
        ...templateData.meta,
        current_user: currentUser,
        current_datetime: moment().utc().format(),
      },
      transaction: {
        ...line,
        customer: templateData.customer,
        location: templateData.location,
        subsidiary: templateData.subsidiary,
        sale: templateData.transaction,
      },
    };

    return transaction;
  }

  function* getTemplateDataByType(line) {
    let currTemplateData = null;

    for (const type of types) {
      if (currTemplateData) break;
      currTemplateData = yield call(getTemplateData, line, type);
    }

    return currTemplateData;
  }

  const processes = lines.map((line) => call(getTemplateDataByType, line));
  const transactions = yield all(processes);
  const transactionsMap = new Map();

  for (const transaction of transactions) {
    if (!transaction) continue;
    transactionsMap.set(transaction.transaction.id, transaction);
  }

  return transactionsMap;
}

function* buildTemplateIdMap(params) {
  const { isPrintAll, types, deliveryType, locationId, subsidiaryId } = params;

  const templateIdMap = new Map();

  for (const type of types) {
    const typePath = type === 'label' ? `label_${deliveryType}` : type;
    const templateId = yield call(getPrintTemplateId, {
      locationId,
      subsidiaryId,
      docType: DOCTYPE_ORDER_MANAGEMENT_CONTROL_RECORD,
      type: typePath,
    });

    templateIdMap.set(type, templateId);
  }

  if (isPrintAll) {
    const templateId = yield call(getPrintTemplateId, {
      locationId,
      subsidiaryId,
      docType: DOCTYPE_SALE_TRANSACTION,
      type: 'oms',
    });

    templateIdMap.set('sale', templateId);
  }

  return templateIdMap;
}

async function processPdf(app, params) {
  try {
    const { link, templateId, transaction } = params;

    let pdfData;

    if (link) {
      const res = await fetch(link);
      pdfData = await res.arrayBuffer();
    } else {
      const { data } = await apiCommon.generatePdf(app, {
        templateId,
        transaction,
        parser: 'handlebar',
      });

      pdfData = data;
    }

    const pdf = await PDFDocument.load(pdfData);
    return pdf;
  } catch (err) {
    console.error('@sagas_order_management_control_records:processPdf', err.message);
    return null;
  }
}

async function mergePdfs(pdfs, isRaw) {
  const validPdfs = pdfs.filter((pdf) => pdf);

  if (!validPdfs.length) return null;

  const mergedPdf = await PDFDocument.create();

  for (const pdf of pdfs) {
    const indeces = await pdf.getPageIndices();
    const copiedPages = await mergedPdf.copyPages(pdf, indeces);
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  if (isRaw) return mergedPdf;

  const mergedFile = await mergedPdf.save();
  return mergedFile;
}

async function printPdfs(pdfs, fileName) {
  const validPdfs = pdfs.filter((pdf) => pdf);

  if (!validPdfs.length) return false;

  const buffer = await mergePdfs(validPdfs);
  const blob = new Blob([buffer], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.style = 'diplay: none;';
  a.click();
  a.remove();

  window.URL.revokeObjectURL(blob);
  return true;
}
